<template>
    <div class="app-container pa-4">
        <breadcrumbs :breadcrumbs="breadcrumbs"></breadcrumbs>
        <v-row class="mx-n5 mx-sm-0 ">
            <v-col sm="auto" class="hidden-sm-and-down">
                <categories-sidebar
                    ref="categoriesSidebar"
                    :category="categorySystemName"
                    :category-id="categoryID"
                    :total="total"
                    @update-list="updateList"
                    v-if="!isMobile"
                ></categories-sidebar>
            </v-col>
            <v-col>
                <div>
                    <h1 class="text-center" v-if="metaData.header">{{ metaData.header }}</h1>
                    <v-col cols="12">
                        <div class="skeleton-text-short" v-if="listLoading">
                            <v-skeleton-loader type="text"></v-skeleton-loader>
                        </div>
                        <div class="overline text-center" v-else>{{$t('productsFound')}}: {{total}}</div>
                    </v-col>
                    <div class="d-flex justify-space-between justify-md-end">
                        <mobile-filters
                            v-if="isMobile"
                            :category-id="categoryID"
                            :category-name="categorySystemName"
                            :filter-key="filterKey"
                            :total="total"
                            @update-list="updateList"
                        ></mobile-filters>
                        <sort-products @apply-sort="applySort"></sort-products>
                    </div>
                    <product-list-chips
                        :items="categoryChips"
                        chips-key="category"
                        @update-list="updateList"
                    ></product-list-chips>
                    <div class="pt-4" v-if="!metaData.header">
                        <v-skeleton-loader type="text"></v-skeleton-loader>
                    </div>
                    <product-list
                        :loading="listLoading"
                        :products="products"
                        :total="total"
                        :init-page="page"
                        :last-page="lastPage"
                        :is-visible-more-button="loadMore"
                        @show-more="incrementPage"
                        @change-page="setPage"
                    ></product-list>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col xs="12" v-if="seoText.length > 0">
                <v-card flat >
                    <div :class="{'seo-text-box': isHiddenSeoText}" ref="seo-text-box">
                        <v-card-text v-html="seoText" class="product-description">{{ seoText }}</v-card-text>
                        <p class="read-more text-center ma-0" v-if="seoText">
                            <v-btn
                                depressed
                                :text="isHiddenSeoText"
                                :dark="!isHiddenSeoText"
                                :class="{'main--yellow': !isHiddenSeoText}"
                                @click="isHiddenSeoText = !isHiddenSeoText"
                            >
                                {{ $t(isHiddenSeoText ? 'readMore' : 'collapse') }}
                            </v-btn>
                        </p>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>

    import AppBreadcrumbs from '../../../app-front/mixins/AppBreadcrumbs';
    import Breadcrumbs from '../../../app-front/components/breadcrumbs';
    import CategoriesChips from './CategoriesChips';
    import SortProducts from "../../../app-front/components/product/sort"
    import CategoriesSidebar from './CategoriesSidebar';
    import Meta from '../../../app-front/mixins/Meta';
    import MobileFilters from "../../../app-front/components/filters/MobileFilters";
    import ProductList from '../../../app-front/components/product/product-list';
    import ProductListChips from '../../../app-front/components/product/product-list-chips';
    import ProductListSort from '../../../app-front/mixins/ProductListSort';
    import ProductsPagination from '../../../app-front/mixins/ProductsPagination';

    import "./style.scss";

    export default {
        name: "Categories",
        props: ['parent', 'alias'],
        mixins: [
            AppBreadcrumbs,
            Meta,
            ProductListSort,
            ProductsPagination,
        ],
        components: {
            Breadcrumbs,
            CategoriesChips,
            CategoriesSidebar,
            MobileFilters,
            ProductList,
            ProductListChips,
            SortProducts
        },
        data() {
            return {
                category: this.alias,
                chips: {},
                filterKey: 'category',
                metaUrl: '/categories/' + this.alias,
                mobileFilters: false,
                subcategoriesList: [],
                seoTextParams: {
                    category: 'categories',
                    alias: this.alias
                },
                isHiddenSeoText: true,
            }
        },
        computed: {
            breadcrumbsData() {
                return {
                    page: 'categoryPage',
                    data: {
                        category: this.categoryID
                    }
                }
            },
            filters() {
                return this.$store.state.filters.category;
            },
            categorySystemName() {
                return this.alias;
            },
            categoryChips() {
                return this.$store.state.chips.category;
            },
            isMobile() {
                return window.innerWidth <= 959;
            },
            parentSystemName() {
                return this.parent;
            },
            categoryID() {
                const name = this.alias;
                const parentName = this.parent;
                const category = this.$store.getters.categories.find(category => [parentName, name].includes(category.systemName));

                if (!category) {
                    return null;
                }

                if (category.systemName === parentName) {
                    return category.subCategories.find(subCategory => subCategory.systemName === name)?.id;
                }

                return category.id;
            }
        },
        methods: {
            reset() {
                this.updateMetaUrl();
                this.setPage(1);
                this.getMetaData();
                this.updateSeoText();
                this.getSeoText();
                this.$store.commit('resetChips', {key: this.filterKey});
            },
            updateBreadcrumbs() {
                this.setBreadcrumbs();
            },
            updateList() {
                if (this.$refs.categoriesSidebar) {
                    this.$refs.categoriesSidebar.setSubcategories()
                }
                this.updateProducts();
            },
            updateMetaUrl() {
                this.metaUrl = '/categories/' + this.alias;
            },
            updateSeoText() {
                this.$set(this.seoTextParams, 'alias', this.alias)
            }
        },
        created() {
            this.$store.commit('resetChips', this.filterKey);
        },
        watch: {
            categoryID() {
                this.updateBreadcrumbs();
            },
            categorySystemName() {
                this.reset();
            },
            parentSystemName() {
                if (!this.listLoading) {
                    this.reset();
                }
            },
            isHiddenSeoText(value) {
                if (value) {
                    const bodyRect = document.body.getBoundingClientRect();
                    const elementRect = this.$refs['seo-text-box'].getBoundingClientRect()

                    if (elementRect.top < 0) {
                        window.scrollTo({
                            top: elementRect.top - bodyRect.top - 50
                        })
                    }
                }
            },
        }
    }
</script>

<style scoped>
    .seo-text-box {
        max-height: 250px;
        position: relative;
        overflow: hidden;
    }
    .seo-text-box .read-more {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        padding-top: 60px;

        background-image: linear-gradient(to bottom, transparent, white);
    }
</style>
